import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import IngredientDetailModal from './IngredientDetailModal';
import IngredientPanel from './IngredientPanel';
import CategoryCard from './CategoryCard';
import './IngredientSelector.css';
import config from '../config/ingredient-display-groups.json';
import { buildFloatingTileItems } from './categoryGridLayout';
import { useCategoryGridColumns } from './useCategoryGridColumns';
import { isIngredientCompatibleWithPreparation } from '../utils/preparationCompatibility';
import { readCapacityTipSeen, writeCapacityTipSeen } from '../utils/capacityGuidance';
import { collapseIngredientFamilies } from '../utils/ingredientFamilies';
import { useSynergySuggestions } from '../utils/useSynergySuggestions';

const DISPLAY_GROUPS = config.groups;

function IngredientSelector({
  ingredients,
  selectedIngredients,
  onToggle,
  onQualityChange,
  preparationMethod,
}) {
  const gridRef = useRef(null);
  const columnCount = useCategoryGridColumns(gridRef);
  const [modalIngredient, setModalIngredient] = useState(null);
  const [openGroupKey, setOpenGroupKey] = useState(null);
  const [capacityTipSeen, setCapacityTipSeen] = useState(readCapacityTipSeen);
  const tipShownThisSession = useRef(false);
  const pendingScrollFamilyRef = useRef(null);

  const compatibleIngredients = useMemo(
    () => ingredients.filter((ing) => isIngredientCompatibleWithPreparation(ing, preparationMethod)),
    [ingredients, preparationMethod],
  );

  const byType = useMemo(() => compatibleIngredients.reduce((acc, ing) => {
    if (!acc[ing.type]) acc[ing.type] = [];
    acc[ing.type].push(ing);
    return acc;
  }, {}), [compatibleIngredients]);

  const displayGroups = useMemo(
    () => DISPLAY_GROUPS
      .map((group) => {
        const flat = group.types
          .flatMap((t) => byType[t] || [])
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        return { ...group, ingredients: collapseIngredientFamilies(flat) };
      })
      .filter((g) => g.ingredients.length > 0),
    [byType],
  );

  const {
    tipSuggestions,
    suggestedFamilyKeys,
    showSynergyTip,
    singleSelectedName,
    groupKeyByFamily,
    groupsWithSynergy,
  } = useSynergySuggestions(
    compatibleIngredients,
    selectedIngredients,
    displayGroups,
    preparationMethod,
  );

  useEffect(() => {
    if (openGroupKey !== null || selectedIngredients.length === 0 || displayGroups.length === 0) return;
    const firstId = selectedIngredients[0];
    const match = displayGroups.find((g) =>
      g.ingredients.some((family) => family.variants.some((v) => v.id === firstId))
    );
    if (match) setOpenGroupKey(match.key);
  }, [displayGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (openGroupKey && !displayGroups.find((g) => g.key === openGroupKey)) {
      setOpenGroupKey(null);
    }
  }, [displayGroups, openGroupKey]);

  useEffect(() => {
    if (openGroupKey && !capacityTipSeen) {
      tipShownThisSession.current = true;
    }
    if (!openGroupKey && tipShownThisSession.current && !capacityTipSeen) {
      writeCapacityTipSeen();
      setCapacityTipSeen(true);
    }
  }, [openGroupKey, capacityTipSeen]);

  useEffect(() => {
    const familyKey = pendingScrollFamilyRef.current;
    if (!familyKey || !openGroupKey) return;
    const group = displayGroups.find((g) => g.key === openGroupKey);
    if (!group?.ingredients.some((f) => f.familyKey === familyKey)) return;

    const frame = window.requestAnimationFrame(() => {
      const el = document.querySelector(
        `[data-ingredient-family="${CSS.escape(familyKey)}"]`
      );
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      pendingScrollFamilyRef.current = null;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [openGroupKey, displayGroups]);

  const dismissCapacityTip = () => {
    tipShownThisSession.current = true;
    writeCapacityTipSeen();
    setCapacityTipSeen(true);
  };

  const toggleGroup = (key) => setOpenGroupKey((prev) => (prev === key ? null : key));

  const handleJumpToFamily = useCallback(
    (familyKey) => {
      const groupKey = groupKeyByFamily.get(familyKey);
      if (!groupKey) return;
      pendingScrollFamilyRef.current = familyKey;
      setOpenGroupKey(groupKey);
    },
    [groupKeyByFamily]
  );

  const openIndex = useMemo(
    () => (openGroupKey == null ? -1 : displayGroups.findIndex((g) => g.key === openGroupKey)),
    [displayGroups, openGroupKey]
  );

  const floatingTileItems = useMemo(
    () => buildFloatingTileItems(displayGroups, openIndex, columnCount),
    [displayGroups, openIndex, columnCount]
  );

  const showCapacityTip = Boolean(openGroupKey) && !capacityTipSeen;

  return (
    <div className="ingredient-selector">
      <div
        ref={gridRef}
        className={`category-card-grid${openGroupKey ? ' category-card-grid--has-open' : ''}`}
      >
        {floatingTileItems.map((item) => {
          if (item.type === 'panel') {
            return (
              <IngredientPanel
                key={`${item.group.key}-panel`}
                group={item.group}
                selectedIngredients={selectedIngredients}
                onToggle={onToggle}
                onQualityChange={onQualityChange}
                onOpenModal={setModalIngredient}
                showCapacityTip={showCapacityTip}
                onDismissCapacityTip={dismissCapacityTip}
                showSynergyTip={showSynergyTip}
                synergySuggestions={tipSuggestions}
                synergySelectedCount={selectedIngredients.length}
                synergySingleName={singleSelectedName}
                suggestedFamilyKeys={suggestedFamilyKeys}
                onJumpToFamily={handleJumpToFamily}
              />
            );
          }

          const { group } = item;
          const selectedCount = group.ingredients.filter((family) =>
            family.variants.some((v) => selectedIngredients.includes(v.id))
          ).length;

          return (
            <CategoryCard
              key={group.key}
              group={group}
              selectedCount={selectedCount}
              isOpen={openGroupKey === group.key}
              hasSynergy={groupsWithSynergy.has(group.key)}
              onToggle={toggleGroup}
            />
          );
        })}
      </div>

      {compatibleIngredients.length === 0 && (
        <div className="no-ingredients">
          <p>No ingredients available for {preparationMethod} preparation.</p>
        </div>
      )}

      {modalIngredient && (
        <IngredientDetailModal ingredient={modalIngredient} onClose={() => setModalIngredient(null)} />
      )}
    </div>
  );
}

export default IngredientSelector;
